import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

function NotFoundScreen() {
  const [isAnimated, setIsAnimated] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [bearPosition, setBearPosition] = useState({ x: 50, y: 50 });

  const errorMessages = [
    {
      title: "🏕️ Lost in the Wilderness!",
      subtitle: "You've wandered off the marked trail...",
      description: "Don't panic! Even experienced survivalists take wrong turns. Remember: Stay calm, assess your surroundings, and retrace your steps."
    },
    {
      title: "🧭 Compass Malfunction!",
      subtitle: "Your navigation seems to be off course...",
      description: "When technology fails, trust your instincts. The sun rises in the east and sets in the west. Let's get you back on track!"
    },
    {
      title: "⛺ Campsite Not Found!",
      subtitle: "This clearing doesn't exist on our map...",
      description: "In survival situations, knowing when to turn back is crucial. Let's find you a safe path back to base camp."
    },
    {
      title: "🔦 Signal Lost!",
      subtitle: "We've lost your GPS coordinates...",
      description: "Remember the survival rule of 3s: 3 minutes without air, 3 hours without shelter, 3 days without water. Let's get you back to safety!"
    },
    {
      title: "🎒 Supply Drop Missed!",
      subtitle: "These coordinates lead nowhere...",
      description: "Every survivor faces setbacks. The key is to adapt, improvise, and overcome. Your training continues back at base!"
    }
  ];

  useEffect(() => {
    setIsAnimated(true);
    setCurrentMessage(Math.floor(Math.random() * errorMessages.length));
    
    // Animate the bear moving around
    const interval = setInterval(() => {
      setBearPosition({
        x: Math.random() * 80 + 10,
        y: Math.random() * 60 + 20
      });
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const survivalItems = [
    { icon: '🔥', name: 'Fire', tip: 'Warmth & Signaling' },
    { icon: '💧', name: 'Water', tip: 'Stay Hydrated' },
    { icon: '🏠', name: 'Shelter', tip: 'Protection' },
    { icon: '🍖', name: 'Food', tip: 'Energy Source' },
    { icon: '🩹', name: 'First Aid', tip: 'Medical Care' },
    { icon: '📡', name: 'Signal', tip: 'Get Rescued' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-900 via-green-800 to-amber-900 flex items-center justify-center px-4 py-16 overflow-hidden relative">
      {/* Animated forest background */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute text-6xl animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          >
            {['🌲', '🌳', '🌿', '🍂'][Math.floor(Math.random() * 4)]}
          </div>
        ))}
      </div>

      {/* Wandering bear */}
      <div
        className="absolute text-4xl transition-all duration-3000 ease-in-out"
        style={{
          left: `${bearPosition.x}%`,
          top: `${bearPosition.y}%`,
          transform: 'translate(-50%, -50%)'
        }}
      >
        🐻
      </div>

      <div className="max-w-3xl mx-auto text-center relative z-10">
        {/* Emergency beacon animation */}
        <div className="relative mb-12">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 bg-red-500 rounded-full animate-ping opacity-20" />
            <div className="w-24 h-24 bg-orange-500 rounded-full animate-ping opacity-30 animation-delay-200" />
          </div>
          
          {/* Large 404 with survival theme */}
          <div className="relative">
            <div className="text-8xl sm:text-9xl font-black text-orange-300 tracking-tight mb-4">
              4
              <span className="inline-block mx-2 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-red-600 flex items-center justify-center animate-pulse">
                    <span className="text-white text-3xl sm:text-4xl">🆘</span>
                  </div>
                </div>
                0
              </span>
              4
            </div>
            
            {/* Survival meter */}
            <div className="flex justify-center space-x-2 mb-6">
              {survivalItems.map((item, index) => (
                <div
                  key={index}
                  className={`bg-green-700/50 backdrop-blur-sm rounded-lg p-2 border border-green-400/30 transform hover:scale-110 transition-all duration-300 animate-fade-in`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  title={item.tip}
                >
                  <div className="text-2xl">{item.icon}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Error Messages */}
        <div className="space-y-6 mb-12">
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-orange-100 animate-fade-in">
              {errorMessages[currentMessage].title}
            </h1>
            <p className="text-xl text-orange-200 animate-fade-in">
              {errorMessages[currentMessage].subtitle}
            </p>
          </div>

          <div className="bg-green-900/40 backdrop-blur-sm rounded-2xl p-6 border border-orange-400/30 animate-fade-in">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
            <p className="text-orange-100 text-lg leading-relaxed">
              {errorMessages[currentMessage].description}
            </p>
          </div>

          {/* Survival tips */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="bg-green-800/60 backdrop-blur-sm rounded-xl p-4 border border-green-400/30 hover:shadow-lg hover:bg-green-800/80 transition-all duration-300 group">
              <div className="text-3xl mb-2 group-hover:animate-bounce">🗺️</div>
              <h3 className="font-semibold text-orange-100 mb-1">Training Map</h3>
              <p className="text-sm text-green-200">Navigate survival modules</p>
            </div>
            
            <div className="bg-green-800/60 backdrop-blur-sm rounded-xl p-4 border border-green-400/30 hover:shadow-lg hover:bg-green-800/80 transition-all duration-300 group">
              <div className="text-3xl mb-2 group-hover:animate-bounce">🎮</div>
              <h3 className="font-semibold text-orange-100 mb-1">Simulations</h3>
              <p className="text-sm text-green-200">Practice survival scenarios</p>
            </div>
            
            <div className="bg-green-800/60 backdrop-blur-sm rounded-xl p-4 border border-green-400/30 hover:shadow-lg hover:bg-green-800/80 transition-all duration-300 group">
              <div className="text-3xl mb-2 group-hover:animate-bounce">🏆</div>
              <h3 className="font-semibold text-orange-100 mb-1">Badges</h3>
              <p className="text-sm text-green-200">Earn survival achievements</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
          <Link
            to="/"
            className="group relative px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-red-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300" />
            <span className="relative flex items-center space-x-2">
              <span>🏕️</span>
              <span>Back to Base Camp</span>
            </span>
          </Link>
          
          <Link
            to="/emergency"
            className="px-8 py-4 bg-red-600/80 backdrop-blur-sm text-white rounded-xl font-bold text-lg hover:bg-red-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-red-400/50 animate-pulse"
          >
            <span className="flex items-center space-x-2">
              <span>🚨</span>
              <span>Emergency Mode</span>
            </span>
          </Link>
        </div>

        {/* Survival reminder */}
        <div className="mt-12 p-4 bg-amber-900/40 backdrop-blur-sm rounded-lg border border-amber-600/30">
          <p className="text-amber-200 text-sm font-semibold">
            ⚠️ Remember: S.T.O.P - Sit, Think, Observe, Plan
          </p>
        </div>

        {/* Decorative survival icons */}
        <div className="mt-8 opacity-50">
          <div className="flex justify-center space-x-4">
            {['⛺', '🔥', '🧭', '🎒', '🔦'].map((emoji, index) => (
              <span
                key={index}
                className="text-3xl animate-pulse"
                style={{
                  animationDelay: `${index * 0.3}s`,
                  animationDuration: '2s',
                }}
              >
                {emoji}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotFoundScreen;